/**
 * Page data jQuery object (empty by default).
 */
let $datalayer = jQuery();

/**
 * Helper function for preparing and sanitizing event data.
 *
 * @param {string} d - Data to prepare.
 *
 * @returns {string} Prepared data.
 */
const prepareEventData = (d) => {
  return d.replace(/\n/g, '').trim().toLowerCase();
};

/**
 * Helper function for getting page data from $datalayer.
 * Also prepares/sanitizes data in advance.
 *
 * @param {string} key - Data key (attribute) to get.
 *
 * @returns {string} Prepared data.
 */
const getPageData = (key) => {
  return $datalayer.length ? prepareEventData($datalayer.data(key)) : null;
};

/**
 * Helper function for pushing and logging data layer events.
 *
 * @param {object} event - Event object.
 */
const pushDataLayerEvent = (event) => {
  window.adobeDataLayer = window.adobeDataLayer || [];
  window.adobeDataLayer.push(event);
  console.log('Data layer event:', JSON.stringify(event));
};

/**
 * Make pushDataLayerEvent available globally.
 */
window.pushDataLayerEvent = pushDataLayerEvent;

/**
 * Helper class for navigation-related event details.
 */
class NavigationHelper {
  static localStorageKey = 'clickedNavigationPath';

  /**
   * Gets the path (descending hierarchy) of a $clicked link within
   * a $menu, and saves the path in localStorage. If $clicked and
   * $menu are not provided, removes and returns the path from
   * localStorage instead.
   *
   * @param {jQuery} $clicked - Clicked menu item.
   * @param {jQuery} $menu    - Menu.
   *
   * @returns {string} Pipe-separated path of $clicked link.
   */
  static getClickedPath($clicked, $menu) {
    let path;

    if (!$clicked && !$menu) {
      path = localStorage.getItem(this.localStorageKey);
      localStorage.removeItem(this.localStorageKey);
      return path;
    }

    path = [];

    const menuID = $menu.attr('id');
    const $items = [];

    if ('main-menu' === menuID) {
      $clicked
        .parentsUntil($menu)
        .filter('li')
        .children('a')
        .each((index, element) => {
          $items.push(jQuery(element));
        });
    } else if ('mobile-menu' === menuID) {
      $items.push(...this.getMobileMenuItems($clicked));
    }

    if ($items.length) {
      $items.forEach(($item) => {
        path.unshift(prepareEventData($item.text()));
      });
    }

    path = path.join(' | ');

    localStorage.setItem(this.localStorageKey, path);

    return path;
  }

  /**
   * Recursively retrieves a clicked mobile menu $item and its
   * ancestors, based on the unconventional structure of mmenu.
   *
   * @param {JQuery} $item  - Current menu item.
   * @param {Array}  $items - Recursed menu items.
   *
   * @returns {Array} of jQuery objects representing menu items.
   */
  static getMobileMenuItems($item, $items = []) {
    const panelID = $item.closest('.mm-panel').attr('id');
    const parentID = $item.closest('.mm-panel').data('mm-parent');
    const $parentItem = parentID ? jQuery(`#${parentID}`) : null;

    $items.push($item);

    if ($parentItem && $parentItem.length) {
      return this.getMobileMenuItems($parentItem.find(`a[href="#${panelID}"]`), $items);
    }

    return $items;
  }
}

const bindDataLayerEvents = () => {
  (($) => {
    $(() => {
      const $body = $('body');
      const path = $body.data('path');

      /* Populate page data jQuery object. ***************************/
      $datalayer = $('#datalayer');

      /* Default values for all events. ******************************/
      const country = 'CA';
      const state = 'Unknown';
      const language = getPageData('language');

      /**
       * event: 'pageLoad'
       *
       * Some templates implement their own 'pageLoad' events in Liquid,
       * so this may be conditional based on path (or other variables).
       */
      if (!path.includes('/products') && !path.includes('/cart')) {
        window.addEventListener('load', () => {
          const event = {
            event: 'pageLoad',
            page: {
              name: getPageData('page-name'),
              title: document.title,
              category: 'content',
            },
            attributes: {
              country,
              state,
              language,
            },
          };

          const section = getPageData('page-section');

          if (section) {
            event.page.section = section;
          }

          const clickedNavigationPath = NavigationHelper.getClickedPath();

          if (clickedNavigationPath) {
            event.page.navigation = clickedNavigationPath;
          }

          const $registrationForm = $('.section--registration-form');

          if ($registrationForm.length) {
            $.extend(true, event, {
              page: {
                name: 'register',
                category: 'account',
              },
              account: {
                type: 'patient',
              },
            });
          }

          pushDataLayerEvent(event);
        });
      }

      /* event: 'navigation click' ***********************************/
      const $mainMenu = $('#main-menu');
      const $mobileMenu = $('#mobile-menu');

      if ($mainMenu.length || $mobileMenu.length) {
        $mainMenu.add($mobileMenu).on('click', 'a', (e) => {
          e.preventDefault();

          const $menu = $(e.delegateTarget);
          const $clicked = $(e.currentTarget);
          const href = $clicked.attr('href');
          const target = $clicked.attr('target');

          if (!href || !href.length) return;

          if (!/^#/.test(href)) {
            const clickedNavigationPath = NavigationHelper.getClickedPath($clicked, $menu);
            const event = {
              event: 'navigation click',
              attributes: {
                eventDetail: clickedNavigationPath,
                eventLocation: 'plp',
              },
            };

            pushDataLayerEvent(event);

            if (target === '_blank') {
              window.open(href, target);
            } else {
              window.location = href;
            }
          }
        });
      }

      /* event: 'link click' (anchors) *******************************/
      $('#main a[href^="#"]').on('click', (e) => {
        const $target = $(e.target);

        const event = {
          event: 'link click',
          attributes: {
            eventDetail: `anchor | ${prepareEventData($target.text())}`,
          },
        };

        pushDataLayerEvent(event);
      });

      /* event: 'video start' ****************************************/
      $('[data-video-src]').on('click', (e) => {
        const $target = $(e.target);

        const event = {
          event: 'video start',
          attributes: {
            eventDetail: prepareEventData($target.text()),
          },
        };

        pushDataLayerEvent(event);
      });

      const $contactForm = $('form#contact_form');

      /* event: 'action' (contact form submit), 'email subscription' */
      if ($contactForm.length)
        (() => {
          const $optIn = $contactForm.find('input[name="contact[opt_in]"]');

          $contactForm.on('submit', (e) => {
            const submitEvent = {
              event: 'action',
              attributes: {
                eventDetail: 'contact us',
                eventLocation: getPageData('page-name'),
              },
            };

            pushDataLayerEvent(submitEvent);

            if ($optIn.length && $optIn.is(':checked')) {
              const optInEvent = {
                event: 'email subscription',
                attributes: {
                  eventDetail: 'email opt in',
                  eventLocation: 'contact us',
                },
              };

              pushDataLayerEvent(optInEvent);
            }
          });
        })();
    });
  })(jQuery);
};

export { getPageData, prepareEventData, pushDataLayerEvent, bindDataLayerEvents };
